import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../ThemeContext.tsx';

const PageWrapper = ({ 
  children, 
  header = 6, 
  footer = 6, 
  className = "",
  backgroundType = "gradient" // "gradient", "white", "gray"
}) => {
  const { isDarkMode } = useTheme();

  const getBackgroundClass = () => {
    switch (backgroundType) {
      case "white":
        return "bg-white";
      case "gray":
        return "bg-gray-50";
      case "gradient":
      default:
        return isDarkMode 
          ? 'bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900' 
          : 'bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50';
    }
  };

  return (
    <div className={`${getBackgroundClass()} min-h-screen ${className}`}>
      {children}
    </div>
  );
};

// Reusable Hero Section Component
export const HeroSection = ({ 
  children, 
  className = "",
  backgroundType = "gradient",
  padding = "py-16 lg:py-20",
  minHeight = "min-h-screen"
}) => {
  const getHeroBackground = () => {
    switch (backgroundType) {
      case "white":
        return "bg-white";
      case "gradient":
      default:
        return "bg-gradient-to-br from-gray-50 via-white to-blue-50";
    }
  };

  return (
    <section className={`relative ${minHeight} flex items-center justify-center overflow-hidden ${getHeroBackground()} pt-[50px] ${className}`}>
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-32 h-32 lg:w-48 lg:h-48 bg-gradient-to-r from-blue-300/10 to-blue-400/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 lg:w-60 lg:h-60 bg-gradient-to-r from-gray-300/10 to-gray-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {children}
      </div>
    </section>
  );
};

// Reusable Section Component
export const Section = ({ 
  children, 
  className = "",
  backgroundType = "white",
  padding = "py-20"
}) => {
  const getSectionBackground = () => {
    switch (backgroundType) {
      case "gray":
        return "bg-gray-50";
      case "gradient":
        return "bg-gradient-to-br from-gray-50 via-white to-blue-50";
      case "dark":
        return "bg-gradient-to-br from-gray-900 via-blue-900 to-gray-800";
      case "white":
      default:
        return "bg-white";
    }
  };

  return (
    <section className={`${padding} ${getSectionBackground()} ${className}`}>
      <div className="w-full px-2 lg:px-4 xl:px-6 max-w-8xl mx-auto">
        {children}
      </div>
    </section>
  );
};

// Reusable Badge Component
export const Badge = ({ 
  children, 
  variant = "blue",
  className = ""
}) => {
  const getBadgeStyles = () => {
    switch (variant) {
      case "green":
        return "bg-gradient-to-r from-green-600 to-emerald-600 text-white";
      case "purple":
        return "bg-gradient-to-r from-purple-600 to-pink-600 text-white";
      case "cyan":
        return "bg-gradient-to-r from-blue-600 to-cyan-600 text-white";
      case "blue":
      default:
        return "bg-gradient-to-r from-blue-600 to-blue-700 text-white";
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={`inline-block ${getBadgeStyles()} px-4 py-2 rounded-full text-sm font-bold mb-4 ${className}`}
    >
      {children}
    </motion.div>
  );
};

// Reusable Heading Component
export const Heading = ({ 
  children, 
  level = 2,
  className = "",
  variant = "default"
}) => {
  const getHeadingStyles = () => {
    const baseStyles = "font-bold mb-6";
    
    switch (level) {
      case 1:
        return `text-3xl sm:text-4xl lg:text-5xl xl:text-6xl ${baseStyles}`;
      case 2:
        return `text-2xl sm:text-3xl lg:text-4xl ${baseStyles}`;
      case 3:
        return `text-xl sm:text-2xl lg:text-3xl ${baseStyles}`;
      default:
        return `text-lg sm:text-xl lg:text-2xl ${baseStyles}`;
    }
  };

  const getVariantStyles = () => {
    switch (variant) {
      case "gradient":
        return "bg-gradient-to-r from-gray-800 via-blue-700 to-gray-800 bg-clip-text text-transparent";
      case "white":
        return "text-white";
      case "default":
      default:
        return "text-gray-900";
    }
  };

  const HeadingTag = `h${level}`;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.2 }}
    >
      <HeadingTag className={`${getHeadingStyles()} ${getVariantStyles()} ${className}`}>
        {children}
      </HeadingTag>
    </motion.div>
  );
};

// Reusable Card Component
export const Card = ({ 
  children, 
  className = "",
  hover = true,
  padding = "p-6"
}) => {
  const hoverClasses = hover 
    ? "hover:bg-white/90 transition-all duration-500 hover:scale-105 shadow-xl hover:shadow-2xl" 
    : "shadow-lg";

  return (
    <div className={`bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl ${padding} ${hoverClasses} relative overflow-hidden ${className}`}>
      {children}
      {hover && (
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] pointer-events-none"></div>
      )}
    </div>
  );
};

// Reusable Icon Component
export const IconWrapper = ({ 
  icon: Icon, 
  size = 24,
  gradient = "from-blue-600 to-blue-700",
  className = ""
}) => {
  return (
    <div className={`w-12 h-12 bg-gradient-to-r ${gradient} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 ${className}`}>
      <Icon className="text-white" size={size} />
    </div>
  );
};

// Reusable CTA Section Component
export const CTASection = ({ 
  title,
  description,
  primaryButton,
  secondaryButton,
  className = ""
}) => {
  return (
    <Section backgroundType="dark" className={className}>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="text-center max-w-4xl mx-auto"
      >
        <Heading level={2} variant="white" className="mb-6">
          {title}
        </Heading>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-xl text-gray-300 mb-8 leading-relaxed"
        >
          {description}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          {primaryButton}
          {secondaryButton}
        </motion.div>
      </motion.div>
    </Section>
  );
};

export default PageWrapper;
